
/**
  * centralized control for the bundles
  *	- keeps track of what step we're on
  * - toggles the view between overview, wizard, and review
  * - collects and returns all selected variants in the wziard
  */
  
class LMProductBundle extends HTMLElement {

  #main;
  #bundleSection;
  #wizardSection;
  #reviewSection;
  #startBtns;
  #steps = [];
  
  constructor() {

    super();

    this.dataset.step = '0';

    this.#main = document.querySelector('main');
    this.#bundleSection = document.querySelector('.lm-product-bundle-section');
    this.#wizardSection = document.querySelector('.lm-product-bundle-wizard-section');
    this.#reviewSection = document.querySelector('.lm-product-bundle-review-section');
    this.#steps = this.querySelectorAll('product-bundle-step');

    //setup our buttons to start into the bundle
    this.#startBtns = this.#bundleSection.querySelectorAll('.create-bundle-tools button');
    this.#startBtns.forEach( btn => btn.addEventListener('click',() => this.setStep('1') ) );

    this.setStep('0');
    
    //kick this off the main thread so everything can init first
    setTimeout( () => this.checkInventory() ,1);

  };

  getStep()
  {
    return this.dataset.step;
  };
  
  setStep(val)
  {
    const oldStep = this.dataset.step;

    //if it's zero, then go back to the intro
    if (val == '0')
    {
      this.#main.dataset.lmBundleMode = 'overview';
    }
    //show the review page
    else if (val > this.#steps.length)
    {
      this.#main.dataset.lmBundleMode = 'review';
    }
    //show the wizard
    else
    {
      this.#main.dataset.lmBundleMode = 'wizard';
    }

    //store our location and trigger a change event
    this.dataset.step = val;

    const evt = new CustomEvent('step:change',{detail:{step:val}});
    this.dispatchEvent(evt);

    //scroll the now-visible section into view on real step transitions
    //(skips the initial setStep('0') call on page load)
    if (oldStep !== String(val))
    {
      const mode = this.#main.dataset.lmBundleMode;
      const target = mode === 'wizard' ? this.#wizardSection
                   : mode === 'review' ? this.#reviewSection
                   : this.#bundleSection;
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    return this;
  };

  nextStep()
  {
    const nextStep = parseInt( this.getStep() ) + 1;
    if (nextStep <= this.#steps.length + 1) this.setStep(nextStep);
  };
  
  prevStep()
  {
    const prevStep = parseInt( this.getStep() ) - 1;
    if (prevStep >= 0) this.setStep(prevStep);
  };

  /**
    * collects all the selected variants from our wizard
    */
  collectVariants()
  {
    const activePickers = this.#wizardSection.querySelectorAll('bundle-product.active bundle-product-variants');
    const variants = [];
    
    activePickers.forEach( picker => {
      variants.push( picker.selectedVariant() );
    });

    return variants;    
  };

  /**
    *	makes sure there's at least one possible selection for every step 
    */
  checkInventory()
  {
    let hasInventory = true;
    
    this.#wizardSection.querySelectorAll('product-bundle-step').forEach( step => {
      if (!step.hasInventory()) hasInventory = false;
    });      

    if (!hasInventory)
    {
      this.#startBtns.forEach( btn => {
        btn.disabled = true;
        btn.innerText = 'Out of Stock';
      });
    }
  };
};

/**
  * navigation bar
  * - displays step progress through the wizard and review
  * - allows navigation to different steps
  * - updated by events in product-bundle
  */
class LMProductBundleNav extends HTMLElement {

  #productBundle;

  constructor() {

    super();

    this.#productBundle = document.querySelector('section.lm-product-bundle-wizard-section product-bundle');
    this.#productBundle.addEventListener('step:change',() => this.updateDisplay() );
       
    this.querySelectorAll('button').forEach( btn => {
    
      btn.addEventListener('click',() => {
        this.#productBundle.setStep( btn.parentNode.dataset.step );
      } );
    });
    
  };

  updateDisplay()
  {
    const curStep = this.#productBundle.getStep();
    
    const oldNav = this.querySelector('.product-bundle-navigation-item.active');
    if (oldNav) oldNav.classList.remove('active');
    
    const curNav = this.querySelector('.product-bundle-navigation-item[data-step="' + curStep + '"]');
    if (curNav) curNav.classList.add('active');
  };
  
};

/**
  * product bundle step
  * - displays an individual step including all possible products for that step
  * - responds to step:change events to show/hide oneself based on the current slide number
  */
class LMProductBundleStep extends HTMLElement {

  #productSelector;
  #productBundle;
  #cards;
  
  constructor() {

    super();

    this.#productBundle = this.closest('product-bundle');    
    this.#productSelector = this.querySelector('select.bundle-product-select');
    this.#cards = this.querySelectorAll('bundle-product');
    
    this.#productBundle.addEventListener('step:change',(evt) => this.updateView(evt) );

    this.updateView();

    //make sure whatever product is showing is available
    setTimeout( () => this.checkInventory(),1);
  };

  //selects a new product to display within the step
  updateView(evt)
  {
    //if we aren't the current step, nothing to do.  Maybe later we change this so 
    //we are responsible for showing/hiding ourselves...
    if (this.dataset.step == this.#productBundle.getStep())
    {
      this.dispatchEvent( new Event('step:show') );
      this.classList.add('active');
    }
    else
    {
      this.classList.remove('active');
    }
  };

  //disable an individual option for the product selector by product shortcode
  disableSelectOption(shortcode)
  {
    const options = this.#productSelector.options;
    
    for (let i=0; i<options.length; i++)
    {
      if (options[i].value == shortcode)
      {
        options[i].disabled = true;
        options[i].textContent += ' (Out of Stock)';
        break;
      }
    }
  };

  /**
    * makes sure a product with at least one variant in stock in showing
    */
  checkInventory()
  {
    const curHandle = this.#productSelector.value;

    //disable any selector options that don't have inventory
    let firstAvailable = null;
        
    this.#cards.forEach( card => {
    
      if (!card.hasInventory()) this.disableSelectOption(card.dataset.productHandle);
      else if (!firstAvailable) firstAvailable = card.dataset.productHandle;
    
    });
    
    if (this.#productSelector.value != firstAvailable)  
    {
      this.#productSelector.value = firstAvailable;
      this.#productSelector.dispatchEvent( new Event('change') );
    }

  };

  hasInventory()
  {
    let hasInventory = false;
    
    //only one card needs inventory for this to work
    this.#cards.forEach( card => {
      if (card.hasInventory()) hasInventory = true;
    });

    return hasInventory;
  };



};

/**
  * manages the individual product card within the step
  */
class LMBundleProductCard extends HTMLElement {

  #productStep;
  #productSelector;
  #product;
    
  //variant picker, media display (inline and modal), what else?
  constructor() {

    super();

    const prodCont = this.querySelector('script[type="application/json"][data-source="product"]');
    this.#product = JSON.parse( prodCont.textContent || prodCont.innerText );

    //cleanup the html by removing the json after we're loaded
    prodCont.parentNode.removeChild(prodCont);
    
    this.#productSelector = this.closest('product-bundle-step').querySelector('select.bundle-product-select');
    this.#productSelector.addEventListener('change',() => this.updateDisplay() );

    //metafields - code is here but these arent being used currently
    this.setupMetafields();
    this.setupVisibility();

    //resize the veritical strip when the card is displayed
    this.closest('product-bundle-step').addEventListener('step:show',() => {
      setTimeout( () => this.checkThumbHeight(), 10);
    });
    
    window.addEventListener('resize',() => this.checkThumbHeight() );
    this.updateDisplay();
  };

  getProduct() { return this.#product; };  

  updateDisplay()
  {
    const selVal = this.#productSelector.value;
    
    if (selVal == this.dataset.productHandle)
    {
      this.classList.add('active');

      //relocate the product selector in the product info itself for display consistency
      const prodsel = this.querySelector('.bundle-product-selector');
      this.querySelector('product-info').prepend( this.#productSelector.parentNode );

      this.checkThumbHeight();
    }
    else
    {
      this.classList.remove('active');
    }
  };

  hasInventory()
  {
    let hasInventory = false;
    
    this.#product.variants.some( variant => {
      
      //at least one is still available
      if (variant.available == true)
      {
        hasInventory = true;
        return true;
      }
    
    });

    return hasInventory;  
  };

  setupMetafields()
  {
    //
    this.hideEmptyAccordions();
    
    //
    this.querySelectorAll('.product__accordion').forEach( item => {
      const tf = item.querySelector('.metafield-multi_line_text_field');
      if (tf) tf.innerHTML = tf.textContent;
    });
  };

  hideEmptyAccordions()
  {
    const accordions = this.querySelectorAll('.product__accordion');

    accordions.forEach( acc => {

      const content = acc.querySelector('.accordion__content');
      if (content.textContent.trim().length == 0)
      {
        acc.style.display = 'none';
      }  
    })    
  };

  setupVisibility()
  {
    this.querySelectorAll('.thumbnail-list button.thumbnail').forEach( item => {
      item.addEventListener('click', (evt) => this.checkVisibility(evt) );
    });
    
  };

  checkVisibility(evt)
  {    
    //we only need to work on the vertical slider
    if (window.innerWidth < 990) return;

    const item = evt.target.closest('li');
    item.scrollIntoView({behavior: "smooth", block: "nearest", inline: "nearest"});    
  };

  checkThumbHeight()
  {    
    //we only need to work on the vertical slider
    if (window.innerWidth < 990) return;

    const sliderComp = this.querySelector('slider-component.thumbnail-slider');
    const sliderImg = this.querySelector('ul.product__media-list');

    if (sliderComp) sliderComp.style.maxHeight = sliderImg.clientHeight + 'px';
  };

};

/**
  * controls all the option selections for one product
  */
class LMBundleProductVariants extends HTMLElement {

  #card;
  #mediaGallery;
  #selectedVariant = null;
      
  constructor() {

    super();

    this.#card = this.closest('bundle-product');   
    this.#mediaGallery = this.#card.querySelector('media-gallery');

    //watch all of the radio buttons, on change we need to set the current matching variant
    this.querySelectorAll('input[type="radio"]').forEach( radio => {
      radio.addEventListener('change',(evt) => this.handleOptionChange(evt) );
    });

    setTimeout( () => this.handleOptionChange(), 1);
    //this.updateSelectedVariant();
  };

  handleOptionChange(evt)
  {
    this.updateOptionAvailability();
    this.updateSelectedVariant();
    this.updateOptionSort();
    
    //not sure if we trigger this all the time or not?
    if (evt) this.dispatchEvent( new Event('options:updated') );
  };

  getSelectedOptions()
  {
    const selectedOptions = {};
    const productOptions = this.#card.getProduct().options;        

    // Gather selected options based on checked radio buttons
    productOptions.forEach(option => {
        const checkedRadio = this.querySelector(`input[name="${option}"]:checked:not(:disabled)`);
        if (checkedRadio) {
            selectedOptions[option] = checkedRadio.value;
        }
    });

    return selectedOptions;

  };

  /**
    * updates all the available options based on the selections
    */
  updateOptionAvailability()
  {
    const productOptions = this.#card.getProduct().options;        
    const variants = this.#card.getProduct().variants;
    const selectedOptions = this.getSelectedOptions();

    // Gather selected options based on checked radio buttons
    productOptions.forEach( (option,optionIdx) => {
    
      //get all the radios with this option
      const optionRadios = this.querySelectorAll(`input[name="${option}"]`);

      //get all selected options before this one
      const tmpVals = Object.values(selectedOptions);
      tmpVals.length = optionIdx;		//shorten the array to just be everything before this option

      const matchingVariants = variants.filter(variant => {
        // Check if every value in our selectedOptions matches up to optionIdx
        return Object.keys(selectedOptions).every((key, idx) => {
          if (idx >= optionIdx) return true; // Only check options before the current optionIdx
          return selectedOptions[key] === variant.options[idx];
        });
      });

      //show/hide the radios based on previous selections
      optionRadios.forEach( radio => {
        
        // Directly determine availability and set radio button state
        const isAvail = matchingVariants.some(variant => variant.options[optionIdx] === radio.value && variant.available);
        
        const radioOption = radio.closest('bundle-product-variant-option');
        
        if (isAvail) radioOption.enable();
        else radioOption.disable();
        
      });

    });

  };


  updateOptionSort()
  {
    const productOptions = this.#card.getProduct().options;        
    const variants = this.#card.getProduct().variants;

    productOptions.forEach( (option,optIdx) => {

      //for now, just do this for color
      if (option == 'Color')
      {
        const optionRadios = this.querySelectorAll(`input[name="${option}"]`);
        const obj = {};
  
        variants.forEach(variant => {
          const val = variant.options[optIdx];
          obj[val] = obj[val] ? obj[val] + (variant.available ? 1 : 0) : (variant.available ? 1 : 0);
        });

        const sorted = Object.entries(obj).sort((a, b) => b[1] - a[1]).map(el => el[0]);
  
        sorted.forEach((val, idx) => {
          const radio = Array.from(optionRadios).find(r => r.value === val);
          radio.parentNode.style.order = idx;

          //set it to out of stock if no options are available for it
          radio.parentNode.classList.toggle('out-of-stock', obj[val] === 0);
        });
      }
    });
  };

  selectedVariant()
  {
    return this.#selectedVariant;
  };

  updateSelectedVariant()
  {
    const selectedOptions = Object.values( this.getSelectedOptions() );
    const productData = this.#card.getProduct();

    if (selectedOptions.length == '0') return;

    productData.variants.some( variant => {
    
      const isMatch = selectedOptions.every((optVal, idx) => variant.options[idx] === optVal);

      if (isMatch) 
      {
        this.#selectedVariant = variant;
        this.#selectedVariant.product = productData;			//store a link to the product in the variant
        return true;
      } 

    });

    //there's featured media, so update the gallery picture    
    if (this.#selectedVariant?.featured_media && this.#mediaGallery)
    {
      //I'm trying not to have to replace media-gallery, so loop through our image buttons and find
      //one with the right mediaId, then its parent has the actual element media id (which includes a section id).
      //May need to duplicate anyways since it's creating multiple galleries with duplicate ids
      
      const mediaId = this.#selectedVariant?.featured_media.id;

      const mediaBtn = this.#mediaGallery.querySelector('.product__media-list button[data-media-id="' + mediaId + '"]');
      const elemId = mediaBtn.closest('li').dataset.mediaId;

      this.#mediaGallery.setActiveMedia(elemId);
    }

  };


};

/**
  * controls the selections within one product option
  */
class LMBundleVariantOptions extends HTMLElement {

  #card;
  #picker;
  #label;
  #defaultText;
  
  constructor()
  {
    super();

    this.#card = this.closest('bundle-product');
    this.#picker = this.closest('bundle-product-variants');
    this.#label = this.querySelector('legend');
    this.#defaultText = this.#label.textContent;
    
    //add a change handler for the options
    this.querySelectorAll('input[type="radio"]').forEach( radio => {
      radio.addEventListener('change',(evt) => this.handleChange(evt) );
    });

    this.#picker.addEventListener('options:updated',() => this.updateOptions() );

    //const firstEnabled = this.querySelector('input[type="radio"]:not(:disabled)');
    //if (firstEnabled) firstEnabled.checked = true;

    this.updateView();      
  };

  updateOptions()
  {
    //get our checked radio btn.  If there isn't one, it was unchecked when it was disabled
    let radio = this.querySelector('input[type="radio"]:checked:not(:disabled)');
    
    if (!radio)
    {
      //get the first one that's not disabled
      radio = this.querySelector('input[type="radio"]:not(:disabled)');

      if (radio)
      {
        const radioOption = radio.closest('bundle-product-variant-option');
        radioOption.select();      
      }
    }
  };

  sortAvailableOptions()
  {
  };

  handleChange(evt)
  {
    this.updateView();
  };

  updateView()
  {
    const curSel = this.querySelector('bundle-product-variant-option.selected');
    if (curSel) curSel.classList.remove('selected');

    const curChecked = this.querySelector('input[type="radio"]:checked:not(:disabled)');
    if (curChecked) curChecked.closest('bundle-product-variant-option').classList.add('selected');

    const labelText = (curChecked) ? this.#defaultText + ': ' + curChecked.dataset.optionLabel : this.#defaultText;
    this.#label.textContent = labelText;
  };

};

/**
  * manages a product option radio button and its label
  */
class LMBundleVariantOption extends HTMLElement {

  #label;
  #form;
  
  constructor()
  {
    super();
    this.#label = this.querySelector('label');
    this.#form = this.querySelector('input');

    this.#label.addEventListener('click',(evt) => this.handleClick(evt) );
    this.#form.addEventListener('change',(evt) => this.handleChange(evt) );  
  };

  enable()
  {
      this.classList.remove('disabled');
      this.#form.classList.remove('disabled');
      this.#form.disabled = false;
  };
  
  disable()
  {
      this.classList.add('disabled');
      this.classList.remove('selected');
      
      this.#form.classList.add('disabled');
      this.#form.checked = false;
      this.#form.disabled = true;
  };

  select(evt)
  {
    //this.classList.add('selected');
    this.#form.checked = true;
    this.#form.dispatchEvent( new Event('change',{bubbles:true}) );
  };
  
  handleClick(evt)
  {
    if (this.#form.disabled == true) return false;
    
    this.select(evt);
  };
  
  handleChange(evt)
  {
  };

}

/**
  * the navigation bar for the wizard
  */
class LMProductBundleWizardNav extends HTMLElement {

  constructor()
  {
    super();
        
    this.querySelectorAll('button').forEach( btn => {
      btn.addEventListener('click',(evt) => this.handleClick(evt) );   
    });
  };

  handleClick(evt)
  {
    if (evt.target.dataset.step == 'back')
    {
      this.closest('product-bundle').prevStep();      
    }
    else
    {
      this.closest('product-bundle').nextStep();
    }
  };
};

/**
  * the review section for once the wizard is done
  */
class LMProductBundleReview extends HTMLElement {

  #productBundle;
  #variants = [];
  #variantList;
  #cart;
    
  constructor()
  {
    super();

    this.#productBundle = document.querySelector('section.lm-product-bundle-wizard-section product-bundle');
    this.#variantList = this.querySelector('.bundle-review-variant-list');

    //this.#cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    //we need to update view everytime a step is changed?
    this.#productBundle.addEventListener('step:change',() => this.updateDisplay() );

    this.querySelector('.bundle-review-toolbar button').addEventListener('click',(evt) => this.addToCart(evt) );
  };

  buildCartPacket()
  {
    const variants = this.#productBundle.collectVariants();
    const productId = document.querySelector('.lm-product-bundle-section').dataset.product;
    
    const items = [];
    variants.forEach( variant => {
      items.push({
        quantity: 1,
        id: variant.id,
        properties: {
          _bundle_product_id: productId
        },
      });
    });

    const packet = {
      items: items,
      sections_url: window.location.pathname
    };

    packet.sections = this.#cart ? this.#cart.getSectionsToRender().map((section) => section.id) : [];

    return packet;
  };

  showBusy(isBusy)
  {
    const btn = this.querySelector('.add-to-cart-button');
    const spinner = btn.querySelector('.loading__spinner');
    
    if (isBusy == true)
    {
      btn.classList.add('loading');
      spinner.classList.remove('hidden');
    }
    else
    {
      btn.classList.remove('loading');
      spinner.classList.add('hidden');
    }
  };

  addToCart(evt)
  {
    this.#cart = document.querySelector('cart-drawer');
    
    const btn = evt.target;
    const spinner = btn.querySelector('.loading__spinner');
    const packet = this.buildCartPacket();

    this.showBusy(true);

    //    
    fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(packet)
        })
        .then( response => response.json() )
        .then( response => {

          if (this.#cart)
          {
            //this is needed to make sure the cart renders when it's empty - bug in shopify cart drawer I guess?
            //document.querySelector('cart-drawer').classList.remove('is-empty');
            this.#cart.classList.remove('is-empty');
            
            //update the cart
            this.#cart.renderContents(response);

            //toggles button visibility
            this.classList.add('is-complete');
          }
          
        })
        .catch(err => {
        
          alert('There was an error adding your items to the cart.  Please refresh the page');
          console.log('Cart Error',err);
          
        })
        .finally( resp => {
        
          this.showBusy(false);
        
        });

  };

  updateDisplay()
  {
    this.#variants = this.#productBundle.collectVariants();

    this.#variantList.innerHTML = '';
    
    this.#variants.forEach( (variant,idx) => {

      const stepNum = idx+1;

      this.#variantList.appendChild( this.buildVariant(variant,stepNum) );
    });

    this.updateTotals();
  
  };

  buildVariant(variant,stepNum)
  {
    const row = document.createElement('div');
    row.classList.add('variant-row');

    /**
      * featured image
      */
    const imgCont = document.createElement('div');
    imgCont.classList.add('variant-image');

    const img = document.createElement('img');

    if (variant?.featured_image?.src) img.setAttribute('src',variant.featured_image.src);
    else if (variant?.product?.featured_image) img.setAttribute('src',variant.product.featured_image);

    imgCont.appendChild(img);
    
    row.append(imgCont);

    /**
      * variant info
      */    
    const info = document.createElement('div');
    info.classList.add('variant-info');
    row.appendChild(info);

    const title = document.createElement('div');
    title.classList.add('product-title');
    info.appendChild(title);
    title.innerText = variant.product.title;

    if (variant.product.variants.length > 1)
    {    

	    variant.product.options.forEach( (optName,optIdx) => {
	      
	      const optCont = document.createElement('div');
	      optCont.classList.add('variant-option');
	      info.appendChild(optCont);
	            
	      const optLabel = document.createElement('label');
	      const optVal = document.createElement('span');
	      
	      optCont.appendChild(optLabel);
	      optCont.appendChild(optVal);
	      
	      optLabel.innerText = optName;
	      optVal.innerText = variant.options[optIdx];
	
	    });

    }
    
    /**
      * edit button
      */
    const editCont = document.createElement('div');
    editCont.classList.add('variant-edit');
    
    const btn = document.createElement('button');
    btn.innerText = 'Edit';
    btn.addEventListener('click',() => this.#productBundle.setStep(stepNum) );
        
    editCont.appendChild(btn);
    row.appendChild(editCont);
        
    return row;
  
  };

  formatCurrency(amount)
  {
    const num = parseFloat(amount);

    if (num) 
    {	
      const withDecimals = num.toFixed(2);
      const withoutDecimals = Math.floor(num);
      
      const useNum = (withDecimals > withoutDecimals) ? withDecimals : withoutDecimals;

      return "$" + useNum;
    }
    else
    {
      return "$0.00";
    }

  };

  //sometimes the varinat prices don't have decimal places.  Store settings???
  formatPrice(num)
  {
    return String(num).indexOf('.') == -1 ? parseFloat(num) / 100 : num;
  };
  
  updateTotals()
  {
    const summaryCont = this.querySelector('.bundle-review-summary');
    const totalCont = summaryCont.querySelector('.amount-total .price');
    const discountCont = summaryCont.querySelector('.amount-with-discount .price');
    const savedCont = summaryCont.querySelector('.amount-saved .price');
    
    //load the product's discount json into an object for later 
    const discountScript = this.querySelector('script[type="application/json"][data-source="discount"]');
    const discountData = JSON.parse( discountScript.textContent || discountScript.innerText );
    
    let totalCost = parseFloat('0.0');
    this.#variants.forEach( variant => {
      totalCost += this.formatPrice(variant.price)
    } );

    let discountAmount = 0.0;

    if (discountData?.percentage?.value)
    {
      discountAmount = totalCost * (discountData.percentage.value/100);
    }
    else if (discountData?.fixedAmount?.amount)
    {
      discountAmount = discountData.fixedAmount.amount;
    }

    totalCont.innerText = this.formatCurrency(totalCost);
    discountCont.innerText = this.formatCurrency(totalCost - discountAmount);
    savedCont.innerText = this.formatCurrency(discountAmount);
  };

};

//link our elements to their classes
customElements.define('product-bundle',LMProductBundle);
customElements.define('product-bundle-navigation',LMProductBundleNav);
customElements.define('product-bundle-step',LMProductBundleStep);
customElements.define('product-bundle-wizard-navigation',LMProductBundleWizardNav);
customElements.define('product-bundle-review',LMProductBundleReview);

customElements.define('bundle-product',LMBundleProductCard);
customElements.define('bundle-product-variants',LMBundleProductVariants);
customElements.define('bundle-product-variant-options',LMBundleVariantOptions);
customElements.define('bundle-product-variant-option',LMBundleVariantOption);



/*
class ProductSizeChart {

  #link = null;
  #chart = null;
  #url = null;
  #content = null;

  constructor(url) {

    this.#url = url;
    this.#link = document.querySelector('#size-chart-link');
    this.#chart = document.querySelector('#size-chart');

    const main = document.querySelector('main');
    main.prepend(this.#chart);

    this.#link.addEventListener('click',() => this.toggleChart());
    this.#chart.querySelector('.close').addEventListener('click',() => this.toggleChart());
  };

  chartIsVisible()
  {
    return this.#chart.classList.contains('is-visible');
  };

  showChart()
  {
    this.#chart.classList.add('is-visible');
  };

  hideChart()
  {
    this.#chart.classList.remove('is-visible');
  };
  
  toggleChart()
  {
    if ( this.chartIsVisible() )
    {
      this.hideChart();
    }
    else
    {
      if (this.#content != null)
      {
        this.showChart();
      }      
      else
      {
        //get the product data as json
        fetch(this.#url)
          .then(response => response.text() )
          .then(data => { 

            const container = document.querySelector('.size-chart-content');
            if (container) container.innerHTML = data;

            this.showChart();

          }).catch(e => { console.error(e); } );
      }

    }

  };
  
};

*/
